import { crv, cva } from '@/styled-system/css';

export const buttonRecipe = cva({
  base: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: {
      _motionReduce: 'none',
      _motionSafe: 'box-shadow 200ms ease, background-color 200ms ease',
    },
    boxShadow: {
      base: 'inset 0 0 0 {borderWidths.Button.default} var(--shadow-color)',
      _focusVisible:
        'inset 0 0 0 {borderWidths.Button.default} var(--shadow-color), {shadows.focus}',
    },
  },
  variants: {
    tone: {
      'brand': {},
      'neutral': {},
      'neutral-fixed': {},
      'critical': {},
    },

    emphasis: {
      filled: {},
      outlined: {},
    },

    disabled: {
      true: {},
      false: {
        cursor: 'pointer',
      },
    },

    loading: {
      true: {},
      false: {},
    },

    ...crv('fluid', {
      true: {
        width: '100%',
      },
      false: {
        width: 'auto',
      },
    }),

    onImpact: {
      true: {},
      false: {},
    },

    size: {
      sm: {
        borderRadius: 'Button.sm',
        gap: 'Button.sm.gap.label-toIcon',
        paddingBlock: 'Button.sm.padding.vert',
        paddingInline: 'Button.sm.padding.horz',
      },
      md: {
        borderRadius: 'Button.md',
        gap: 'Button.md.gap.label-toIcon',
        paddingBlock: 'Button.md.padding.vert',
        paddingInline: 'Button.md.padding.horz',
      },
      lg: {
        borderRadius: 'Button.lg',
        gap: 'Button.lg.gap.label-toIcon',
        paddingBlock: 'Button.lg.padding.vert',
        paddingInline: 'Button.lg.padding.horz',
      },
    },
  },
  compoundVariants: [
    {
      loading: false,
      disabled: true,
      css: {
        opacity: 'disabled',
      },
    },
    {
      size: ['sm'],
      css: {
        _after: {
          content: '""',
          position: 'absolute',
          height: 'Button.sm.tapArea',
          minWidth: 'Button.sm.tapArea',
          width: '100%',
          left: '0',
          top: '50%',
          marginTop: 'calc(({sizes.Button.sm.tapArea} / 2) * -1)',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'Button.filled.brand.bg.default',
        },
        boxShadowColor: {
          base: 'Button.filled.brand.border.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'Button.filled.critical.bg.default',
        },
        boxShadowColor: {
          base: 'Button.filled.critical.border.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'Button.outlined.brand.bg.default',
        },
        boxShadowColor: {
          base: 'Button.outlined.brand.border.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'Button.outlined.critical.bg.default',
        },
        boxShadowColor: {
          base: 'Button.outlined.critical.border.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'Button.outlined.neutral.bg.default',
        },
        boxShadowColor: {
          base: 'Button.outlined.neutral.border.default',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'Button.outlined.neutral-fixed.bg.default',
        },
        boxShadowColor: {
          base: 'Button.outlined.neutral-fixed.border.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'Button.onImpact.filled.brand.bg.default',
        },
        boxShadowColor: {
          base: 'Button.onImpact.filled.brand.border.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'neutral',
      css: {
        backgroundColor: {
          base: 'Button.onImpact.outlined.neutral.bg.default',
        },
        boxShadowColor: {
          base: 'Button.onImpact.outlined.neutral.border.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'brand',
      css: {
        backgroundColor: {
          base: 'Button.onImpact.outlined.brand.bg.default',
        },
        boxShadowColor: {
          base: 'Button.onImpact.outlined.brand.border.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'Button.onImpact.filled.critical.bg.default',
        },
        boxShadowColor: {
          base: 'Button.onImpact.filled.critical.border.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'critical',
      css: {
        backgroundColor: {
          base: 'Button.onImpact.outlined.critical.bg.default',
        },
        boxShadowColor: {
          base: 'Button.onImpact.outlined.critical.border.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'Button.onImpact.filled.neutral-fixed.bg.default',
        },
        boxShadowColor: {
          base: 'Button.onImpact.filled.neutral-fixed.border.default',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      css: {
        backgroundColor: {
          base: 'Button.onImpact.outlined.neutral-fixed.bg.default',
        },
        boxShadowColor: {
          base: 'Button.onImpact.outlined.neutral-fixed.border.default',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.filled.brand.bg.hover',
          _active: 'Button.filled.brand.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.filled.brand.border.hover',
          _active: 'Button.filled.brand.border.pressed',
        },
      },
    },
    {
      emphasis: 'filled',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.filled.critical.bg.hover',
          _active: 'Button.filled.critical.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.filled.critical.border.hover',
          _active: 'Button.filled.critical.border.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.outlined.brand.bg.hover',
          _active: 'Button.outlined.brand.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.outlined.brand.border.hover',
          _active: 'Button.outlined.brand.border.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.outlined.critical.bg.hover',
          _active: 'Button.outlined.critical.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.outlined.critical.border.hover',
          _active: 'Button.outlined.critical.border.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.outlined.neutral.bg.hover',
          _active: 'Button.outlined.neutral.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.outlined.neutral.border.hover',
          _active: 'Button.outlined.neutral.border.pressed',
        },
      },
    },
    {
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.outlined.neutral-fixed.bg.hover',
          _active: 'Button.outlined.neutral-fixed.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.outlined.neutral-fixed.border.hover',
          _active: 'Button.outlined.neutral-fixed.border.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.onImpact.filled.brand.bg.hover',
          _active: 'Button.onImpact.filled.brand.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.onImpact.filled.brand.border.hover',
          _active: 'Button.onImpact.filled.brand.border.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'neutral',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.onImpact.outlined.neutral.bg.hover',
          _active: 'Button.onImpact.outlined.neutral.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.onImpact.outlined.neutral.border.hover',
          _active: 'Button.onImpact.outlined.neutral.border.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'brand',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.onImpact.outlined.brand.bg.hover',
          _active: 'Button.onImpact.outlined.brand.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.onImpact.outlined.brand.border.hover',
          _active: 'Button.onImpact.outlined.brand.border.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.onImpact.filled.critical.bg.hover',
          _active: 'Button.onImpact.filled.critical.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.onImpact.filled.critical.border.hover',
          _active: 'Button.onImpact.filled.critical.border.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'critical',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.onImpact.outlined.critical.bg.hover',
          _active: 'Button.onImpact.outlined.critical.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.onImpact.outlined.critical.border.hover',
          _active: 'Button.onImpact.outlined.critical.border.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'filled',
      tone: 'neutral-fixed',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.onImpact.filled.neutral-fixed.bg.hover',
          _active: 'Button.onImpact.filled.neutral-fixed.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.onImpact.filled.neutral-fixed.border.hover',
          _active: 'Button.onImpact.filled.neutral-fixed.border.pressed',
        },
      },
    },
    {
      onImpact: true,
      emphasis: 'outlined',
      tone: 'neutral-fixed',
      disabled: false,
      css: {
        backgroundColor: {
          _hover: 'Button.onImpact.outlined.neutral-fixed.bg.hover',
          _active: 'Button.onImpact.outlined.neutral-fixed.bg.pressed',
        },
        boxShadowColor: {
          _hover: 'Button.onImpact.outlined.neutral-fixed.border.hover',
          _active: 'Button.onImpact.outlined.neutral-fixed.border.pressed',
        },
      },
    },
  ],
});
